import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  canAccessAdmin,
  canAccessClinicalWorkspace,
  getIamAccessState,
  isIamAccessLoading,
  refreshIamAccessProfile,
  subscribeIamAccess,
} from './iamAccess';
import { Button } from '../components/ui/button';

function ForbiddenShell({ title, message }: { title: string; message: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 text-foreground">
      <div className="w-full max-w-xl rounded-3xl border border-border bg-surface-raised p-8 text-center shadow-sm">
        <h1 className="text-xl font-medium">{title}</h1>
        <p className="mt-3 text-sm leading-6 text-text-tertiary">{message}</p>
      </div>
    </div>
  );
}

function useIamAccessRevision(): number {
  const [, setState] = useState(() => getIamAccessState());
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    return subscribeIamAccess((next) => {
      setState(next);
      setRevision((current) => current + 1);
    });
  }, []);

  return revision;
}

function AccessLoadingShell() {
  const [showRetry, setShowRetry] = useState(false);

  useEffect(() => {
    const timeout = window.setTimeout(() => setShowRetry(true), 8_000);
    return () => window.clearTimeout(timeout);
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 text-foreground">
      <div className="w-full max-w-xl rounded-3xl border border-border bg-surface-raised p-8 text-center shadow-sm">
        <h1 className="text-xl font-medium">MarchAgent</h1>
        <p className="mt-3 text-sm leading-6 text-text-tertiary">Loading your workspace…</p>
        {showRetry ? (
          <Button
            className="mt-5"
            size="sm"
            variant="outline"
            onClick={() => void refreshIamAccessProfile()}
          >
            Retry access check
          </Button>
        ) : null}
      </div>
    </div>
  );
}

const ACCESS_DENIED_TITLE = 'MarchAgent';
const ACCESS_DENIED_MESSAGE = 'You do not have access to this workspace.';
const ACCESS_DENIED_HELP = 'Please contact your March Health administrator.';

export function AdminRouteGuard({ children }: { children: React.ReactNode }) {
  useIamAccessRevision();
  if (isIamAccessLoading()) {
    return <AccessLoadingShell />;
  }
  if (!canAccessAdmin()) {
    // Send clinical users to the chat view instead of a dead-end.
    if (canAccessClinicalWorkspace()) {
      return <Navigate to="/chat" replace />;
    }
    return (
      <ForbiddenShell
        title={ACCESS_DENIED_TITLE}
        message={`${ACCESS_DENIED_MESSAGE} ${ACCESS_DENIED_HELP}`}
      />
    );
  }
  return <>{children}</>;
}

export function ClinicalWorkspaceGate({ children }: { children: React.ReactNode }) {
  useIamAccessRevision();
  if (isIamAccessLoading()) {
    return <AccessLoadingShell />;
  }
  if (!canAccessClinicalWorkspace()) {
    // Admin-only users must land on the admin panel, never a clinical dead-end.
    if (canAccessAdmin()) {
      return <Navigate to="/admin" replace />;
    }
    return (
      <ForbiddenShell
        title={ACCESS_DENIED_TITLE}
        message={`${ACCESS_DENIED_MESSAGE} ${ACCESS_DENIED_HELP}`}
      />
    );
  }
  return <>{children}</>;
}
